import { useState } from 'react'
import { supabase } from '../lib/supabase'
import './FeedbackWidget.css'

export default function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [feedbackType, setFeedbackType] = useState<'bug' | 'enhancement'>('bug')
  const [subject, setSubject] = useState('')
  const [details, setDetails] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      if (!subject.trim() || !details.trim()) {
        throw new Error('Subject and details are required')
      }

      const { error } = await supabase.from('feedback').insert([
        {
          type: feedbackType,
          subject,
          details,
          email: email || null,
          status: 'open',
        },
      ])

      if (error) throw error

      setMessage('✅ Thank you! Feedback submitted.')
      setTimeout(() => {
        setSubject('')
        setDetails('')
        setEmail('')
        setFeedbackType('bug')
        setMessage('')
        setIsOpen(false)
      }, 2000)
    } catch (err) {
      setMessage('❌ ' + (err instanceof Error ? err.message : 'Failed to submit'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Floating button */}
      <button
        className="feedback-widget-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Send feedback"
        aria-label="Open feedback form"
      >
        💬
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="feedback-widget-overlay" onClick={() => setIsOpen(false)}>
          <div className="feedback-widget-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>Send Feedback</h3>
              <button
                className="close-button"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="form-group">
                <label>Type</label>
                <div className="type-buttons">
                  <button
                    type="button"
                    className={`type-btn ${feedbackType === 'bug' ? 'active' : ''}`}
                    onClick={() => setFeedbackType('bug')}
                  >
                    🐛 Bug
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${feedbackType === 'enhancement' ? 'active' : ''}`}
                    onClick={() => setFeedbackType('enhancement')}
                  >
                    ✨ Feature
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="feedback-subject">Subject *</label>
                <input
                  id="feedback-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief title..."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="feedback-details">Details *</label>
                <textarea
                  id="feedback-details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Describe what you found or what you'd like to see..."
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="feedback-email">Email (optional)</label>
                <input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>

              {message && (
                <div className={`feedback-message ${message.includes('✅') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="submit-button"
              >
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
